import { forwardRef } from 'react';
import { formatSimpleDate } from '@shared/utils/formatDate';
import safeToBase64Image from '@shared/lib/safeToBase64Image';
import Button from '@shared/components/atoms/Button';
import styles from '@pages/admin/vacation/components/PrintableVacationDetail/PrintableVacationDetail.module.scss';

const PrintableVacationDetail = forwardRef(function PrintableVacationDetail(
  { vacationData, remainVacationData, handlePrint, handleClosePreview },
  ref,
) {
  if (!vacationData) return null;

  const writerBase64 = vacationData.writer_sign
    ? safeToBase64Image(vacationData.writer_sign)
    : null;

  const signerBase64 = vacationData.signer_sign
    ? safeToBase64Image(vacationData.signer_sign)
    : null;
  return (
    <div className={styles.previewPage}>
      <div className={styles.previewContainer}>
        {/* 인쇄 대상 */}
        <div ref={ref} className={styles.printContentWrapper}>
          {/* 화면 미리보기 */}
          <div className={styles.screenPreview}>
            <div className={styles.previewContent}>
              {/* ================= 헤더 영역 ================= */}
              <div className={styles.headerContainer}>
                <div className={styles.headerBox}>
                  <div className={styles.headerTitle}>휴가신청서</div>

                  <div className={styles.approverContainer}>
                    {/* 신청자 */}
                    <div className={styles.signerBox}>
                      <span className={styles.signLabel}>기안자</span>
                      <div className={styles.signContent}>
                        {writerBase64 && (
                          <img src={writerBase64} width={38} height={38} alt="신청자 전자서명" />
                        )}
                      </div>
                    </div>

                    {/* 결재자 */}
                    <div className={styles.signerBox}>
                      <span className={styles.signLabel}>결재자</span>
                      <div className={styles.signContent}>
                        {signerBase64 ? (
                          <img src={signerBase64} width={38} height={38} alt="결재자 전자서명" />
                        ) : (
                          '-'
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* ================= 바디 영역 ================= */}
              <div className={styles.bodyContainer}>
                <table className={styles.tableContainer}>
                  <tbody>
                    <tr>
                      <td>소속</td>
                      <td>{vacationData.writerInfo.team}</td>
                    </tr>
                    <tr>
                      <td>성명</td>
                      <td>{vacationData.writerInfo.name}</td>
                    </tr>
                    <tr>
                      <td>신청내역</td>
                      <td>
                        {vacationData.vacation_type.name || '-'} -
                        {vacationData.cc_type?.name ? ` ${vacationData.cc_type.name}` : ''}
                      </td>
                    </tr>
                    <tr>
                      <td>휴가기간</td>
                      <td>
                        {formatSimpleDate(vacationData.start_date)} ~
                        {formatSimpleDate(vacationData.end_date)} ({vacationData.period}일)
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="2" className={styles.reason}>
                        <div className={styles.reasonContent}>{vacationData.reason}</div>
                      </td>
                    </tr>
                    <tr>
                      <td>첨부파일</td>
                      <td>
                        {Array.isArray(vacationData.fileList) && 0 < vacationData.fileList.length
                          ? vacationData.fileList.map((file, index) => (
                              <div key={index}>
                                <button
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.fileLink}
                                >
                                  {file.name}
                                </button>
                              </div>
                            ))
                          : '없음'}
                      </td>
                    </tr>
                    <tr>
                      <td>참조자</td>
                      <td>
                        {Array.isArray(vacationData.recipientInfos) &&
                        0 < vacationData.recipientInfos.length
                          ? vacationData.recipientInfos.map((r) => r.name).join(', ')
                          : '없음'}
                      </td>
                    </tr>
                    {vacationData.reject_reason && (
                      <tr>
                        <td>코멘트</td>
                        <td>{vacationData.reject_reason}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <div className={styles.statusTableContainer}>
                  <table className={styles.statusTable}>
                    <thead>
                      <tr>
                        <th colSpan="3">발생분</th>
                        <th colSpan="2">사용분(반영)</th>
                        <th colSpan="2">잔여분</th>
                      </tr>
                      <tr>
                        <th>년초 발생분</th>
                        <th>
                          개정 후<br /> 발생분
                        </th>
                        <th>당해 추가분</th>
                        <th>
                          전년
                          <br /> 선사용분
                        </th>
                        <th>당해 사용분</th>
                        <th>개정 후</th>
                        <th className={styles.bold}>총 잔여분</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{remainVacationData.generated.beginningOfYear}</td>
                        <td>{remainVacationData.generated.afterRevision}</td>
                        <td>{remainVacationData.generated.currentYearAdditional}</td>
                        <td>{remainVacationData.used.previousYearPreUsed}</td>
                        <td>{remainVacationData.used.currentYearUsed}</td>
                        <td>{remainVacationData.remaining.afterRevision}</td>
                        <td className={styles.bold}>{remainVacationData.remaining.total}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className={styles.bottomContainer}>
                  <div className={styles.bottomBox}>
                    <div className={styles.bottomTitle}>위와 같이 휴가를 신청합니다.</div>
                    <div className={styles.bottomContent}>
                      {formatSimpleDate(vacationData.start_date, { style: 'korean' })}
                      <br />
                      신청인 : {vacationData.writerInfo.name}
                    </div>
                  </div>
                  <div className={styles.bottomCompany}>
                    (주) {vacationData.writerInfo.company}귀중
                  </div>
                </div>
              </div>
            </div>
            {/* 미리보기 컨트롤 */}
            <div className={styles.previewActions}>
              <Button onClick={handlePrint}>인쇄</Button>
              <Button onClick={handleClosePreview}>닫기</Button>
            </div>
          </div>

          {/* 인쇄용 (화면에서는 숨김) */}
          <div className={styles.printContainer}>
            <div className={styles.printPage}>
              {/* 동일한 콘텐츠 재사용 */}
              <div className={styles.previewContent}>
                {/* ================= 헤더 영역 ================= */}
                <div className={styles.headerContainer}>
                  <div className={styles.headerBox}>
                    <div className={styles.headerTitle}>휴가신청서</div>

                    <div className={styles.approverContainer}>
                      {/* 신청자 */}
                      <div className={styles.signerBox}>
                        <span className={styles.signLabel}>기안자</span>
                        <div className={styles.signContent}>
                          {writerBase64 && (
                            <img src={writerBase64} width={38} height={38} alt="신청자 전자서명" />
                          )}
                        </div>
                      </div>

                      {/* 결재자 */}
                      <div className={styles.signerBox}>
                        <span className={styles.signLabel}>결재자</span>
                        <div className={styles.signContent}>
                          {signerBase64 ? (
                            <img src={signerBase64} width={38} height={38} alt="결재자 전자서명" />
                          ) : (
                            '-'
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* ================= 바디 영역 ================= */}
                <div className={styles.bodyContainer}>
                  <table className={styles.tableContainer}>
                    <tbody>
                      <tr>
                        <td>소속</td>
                        <td>{vacationData.writerInfo.team}</td>
                      </tr>
                      <tr>
                        <td>성명</td>
                        <td>{vacationData.writerInfo.name}</td>
                      </tr>
                      <tr>
                        <td>신청내역</td>
                        <td>
                          {vacationData.vacation_type.name || '-'} -
                          {vacationData.cc_type?.name ? ` ${vacationData.cc_type.name}` : ''}
                        </td>
                      </tr>
                      <tr>
                        <td>휴가기간</td>
                        <td>
                          {formatSimpleDate(vacationData.start_date)} ~
                          {formatSimpleDate(vacationData.end_date)} ({vacationData.period}일)
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="2" className={styles.reason}>
                          <div className={styles.reasonContent}>{vacationData.reason}</div>
                        </td>
                      </tr>
                      <tr>
                        <td>첨부파일</td>
                        <td>
                          {Array.isArray(vacationData.fileList) && 0 < vacationData.fileList.length
                            ? vacationData.fileList.map((file, index) => (
                                <div key={index}>
                                  <button
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.fileLink}
                                  >
                                    {file.name}
                                  </button>
                                </div>
                              ))
                            : '없음'}
                        </td>
                      </tr>
                      <tr>
                        <td>참조자</td>
                        <td>
                          {Array.isArray(vacationData.recipientInfos) &&
                          0 < vacationData.recipientInfos.length
                            ? vacationData.recipientInfos.map((r) => r.name).join(', ')
                            : '없음'}
                        </td>
                      </tr>
                      {vacationData.reject_reason && (
                        <tr>
                          <td>코멘트</td>
                          <td>{vacationData.reject_reason}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <div className={styles.statusTableContainer}>
                    <table className={styles.statusTable}>
                      <thead>
                        <tr>
                          <th colSpan="3">발생분</th>
                          <th colSpan="2">사용분(반영)</th>
                          <th colSpan="2">잔여분</th>
                        </tr>
                        <tr>
                          <th>년초 발생분</th>
                          <th>
                            개정 후<br /> 발생분
                          </th>
                          <th>당해 추가분</th>
                          <th>
                            전년
                            <br /> 선사용분
                          </th>
                          <th>당해 사용분</th>
                          <th>개정 후</th>
                          <th className={styles.bold}>총 잔여분</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{remainVacationData.generated.beginningOfYear}</td>
                          <td>{remainVacationData.generated.afterRevision}</td>
                          <td>{remainVacationData.generated.currentYearAdditional}</td>
                          <td>{remainVacationData.used.previousYearPreUsed}</td>
                          <td>{remainVacationData.used.currentYearUsed}</td>
                          <td>{remainVacationData.remaining.afterRevision}</td>
                          <td className={styles.bold}>{remainVacationData.remaining.total}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className={styles.bottomContainer}>
                    <div className={styles.bottomBox}>
                      <div className={styles.bottomTitle}>위와 같이 휴가를 신청합니다.</div>
                      <div className={styles.bottomContent}>
                        {formatSimpleDate(vacationData.start_date, { style: 'korean' })}
                        <br />
                        신청인 : {vacationData.writerInfo.name}
                      </div>
                    </div>
                    <div className={styles.bottomCompany}>
                      (주) {vacationData.writerInfo.company}귀중
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default PrintableVacationDetail;
